import React,{Suspense,useEffect,useRef} from "react"
import { useLocation } from 'react-router-dom';
import { Outlet } from "react-router-dom";
import 'assets/styles/base.css';
import NavigationBar from "components/base/Navi/navbar";
import usePageScale from 'components/business/usePageScale';
import { getItem, setItem } from 'helpers/localStorageManagement';
import Footer from "components/base/Footer/footer";
import CookieConsent from "components/CookieConsent";
import { Grid } from '@mui/material';
import { useColorMode } from 'context/ColorModeContext';
import { NavCtaProvider } from 'context/NavCtaContext';
import { isCampaignVisit, trackPageView } from 'helpers/tracking';

const Layout = () => {
  let location = useLocation();
  const { mode } = useColorMode();
  // --biz-zoom on the root, for every page: the header scales with the
  // viewport past 1440px on all of them (navbar.css); the .biz-page layouts
  // scale their content by the same factor (business.css).
  usePageScale();

  // Analytics page views for client-side navigation: gtag's config sends
  // one for the first load; every route change after it is reported here,
  // with the full URL. (If the GA4 property's Enhanced Measurement also
  // reports "page changes based on browser history events", turn that off
  // there, or each navigation counts twice.) Events queue in dataLayer
  // until consent loads gtag.js, so nothing is sent without consent.
  const firstLocation = useRef(true);
  useEffect(() => {
    if (firstLocation.current) { firstLocation.current = false; return; }
    trackPageView(location);
  }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // The sign-in pages paint their own background behind the whole shell.
  // They get the same header as every other page: it used to be a separate
  // menu-only bar (HomeNavigator) that never picked up the nav's scaling, so
  // on a wide screen the menu on / stayed at its 1440px size while every
  // other page's grew with the viewport.
  const isHomeBg = ['/', '/landing'].includes(location.pathname);

  useEffect(()=>{
    const referrer = getItem('referrer');
    const userAgent = getItem('userAgent');

    if (!referrer?.length) {
      setItem('referrer', document.referrer);
    }
    if (!userAgent) {
      setItem('userAgent', navigator?.userAgent);
    }
  }, []);

  // Pages whose only job is a single sign-up. The nav keeps the logo and a way
  // in and drops the section menus and the duplicate "Get Started", which on a
  // referral invite compete with the page's own CTA.
  // … and the AI Assistant landing page, but only for a visit that arrived from
  // a campaign (utm_* / click id): paid traffic gets the logo, sign-in and the
  // page's one action, without five menus of ways to leave. Organic visitors
  // to the same URL keep the full site navigation.
  const isConversionPage = location.pathname.startsWith('/refer/')
    || (location.pathname === '/products/ai-assistant' && isCampaignVisit(location.search));

  const homeBgImage = mode === 'dark' ? '/images/home/background.png' : '/images/home/background-white.png';

  return (
    <NavCtaProvider>
    <Grid container className={isHomeBg ? 'home-page-bg' : ''}
      style={isHomeBg ? { backgroundImage: `url('${homeBgImage}')` } : {}}
      width='100%' display='flex' flexDirection='column' justifyContent='center'>
      <NavigationBar minimal={isConversionPage}/>
      {/* route components are code-split (index.js); while one loads, hold the
          page's height so the footer does not jump up under the header */}
      <Suspense fallback={<div style={{ minHeight: '100vh' }} aria-busy='true' />}>
        <Outlet />
      </Suspense>
      <Footer/>
      <CookieConsent/>
    </Grid>
    </NavCtaProvider>
  )
};

export default Layout;